import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Tag, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { productAPI } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    price: '',
    pieces_per_box: '10',
    category: 'basah',
    is_best_seller: false,
    image: null
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const filteredProducts = filterCategory === 'all' 
    ? products 
    : products.filter(p => p.category === filterCategory);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    data.append('name', formData.name);
    data.append('name_en', formData.name_en);
    data.append('description', formData.description);
    data.append('description_en', formData.description_en);
    data.append('price', parseInt(formData.price));
    data.append('pieces_per_box', parseInt(formData.pieces_per_box));
    data.append('category', formData.category);
    data.append('is_best_seller', formData.is_best_seller);
    if (formData.image) {
      data.append('image', formData.image);
    }

    try {
      if (editingProduct) {
        await productAPI.update(editingProduct.id, data);
        toast.success('Product updated successfully!');
      } else {
        await productAPI.create(data);
        toast.success('Product created successfully!');
      }
      
      fetchProducts();
      closeDialog();
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await productAPI.delete(id);
      toast.success('Product deleted!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const openDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        name_en: product.name_en,
        description: product.description,
        description_en: product.description_en,
        price: product.price.toString(),
        pieces_per_box: (product.pieces_per_box || 10).toString(),
        category: product.category || 'basah',
        is_best_seller: !!product.is_best_seller,
        image: null
      });
    } else {
      setEditingProduct(null);
      setFormData({ 
        name: '', 
        name_en: '', 
        description: '', 
        description_en: '', 
        price: '', 
        pieces_per_box: '10', 
        category: 'basah',
        is_best_seller: false,
        image: null 
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
    setFormData({ 
      name: '', 
      name_en: '', 
      description: '', 
      description_en: '', 
      price: '', 
      pieces_per_box: '10', 
      category: 'basah',
      is_best_seller: false,
      image: null 
    });
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'premium':
        return <Badge variant="static" className="bg-amber-500 text-white">Premium</Badge>;
      case 'kering':
        return <Badge variant="static" className="bg-blue-600 text-white">Kering</Badge>;
      case 'basah':
      default:
        return <Badge variant="static" className="bg-green-600 text-white">Basah</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#3E2723]">Product Management</h2>
          <p className="text-sm text-gray-500">Total {filteredProducts.length} produk</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="w-full sm:w-48">
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Filter Jenis" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Jenis</SelectItem>
                <SelectItem value="basah">Bakpia Basah</SelectItem>
                <SelectItem value="kering">Bakpia Kering</SelectItem>
                <SelectItem value="premium">Bakpia Premium</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={() => openDialog()} className="bg-[#B22222] hover:bg-[#8B0000] w-full sm:w-auto">
            <Plus size={18} className="mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="flex flex-col items-center justify-center text-gray-400">
            <Tag size={48} className="mb-4 opacity-20" />
            <p className="text-lg font-medium">Tidak ada produk dalam kategori ini</p>
            <Button 
              variant="link" 
              onClick={() => setFilterCategory('all')}
              className="mt-2 text-[#B22222]"
            >
              Lihat semua produk
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden">
            <div className="relative">
              {product.image_url && (
                <img
                  src={`${BACKEND_URL}${product.image_url}`}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                {getCategoryBadge(product.category)}
                {product.is_best_seller && (
                  <Badge className="bg-rose-500 text-white flex items-center gap-1 border-none shadow-md">
                    <Star size={12} fill="currentColor" />
                    Best Seller
                  </Badge>
                )}
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-lg mb-1">{product.name}</h3>
              <p className="text-xs text-gray-500 mb-2">Isi {product.pieces_per_box || 10} per box</p>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
              <p className="text-[#B22222] font-bold mb-4">Rp {product.price.toLocaleString()}</p>
              
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openDialog(product)} className="flex-1">
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(product.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="block text-sm font-medium mb-2">Name (ID)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">Name (EN)</Label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label className="block text-sm font-medium mb-2">Description (ID)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div>
              <Label className="block text-sm font-medium mb-2">Description (EN)</Label>
              <Textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="block text-sm font-medium mb-2">Price (Rp)</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">Isi per Box</Label>
                <Select 
                  value={formData.pieces_per_box} 
                  onValueChange={(val) => setFormData({ ...formData, pieces_per_box: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih isi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 Pcs</SelectItem>
                    <SelectItem value="15">15 Pcs</SelectItem>
                    <SelectItem value="20">20 Pcs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">Jenis Bakpia</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData({ ...formData, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih jenis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basah">Basah</SelectItem>
                    <SelectItem value="kering">Kering</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2 bg-rose-50 p-3 rounded-lg border border-rose-100">
              <Checkbox 
                id="is_best_seller" 
                checked={formData.is_best_seller}
                onCheckedChange={(checked) => setFormData({ ...formData, is_best_seller: checked })}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="is_best_seller"
                  className="text-sm font-bold text-rose-700 leading-none cursor-pointer flex items-center gap-1"
                >
                  <Star size={14} fill="currentColor" />
                  Tandai sebagai Best Seller
                </Label>
                <p className="text-xs text-rose-600/70">
                  Produk ini akan ditampilkan di halaman utama.
                </p>
              </div>
            </div>

            <div>
              <Label className="block text-sm font-medium mb-2">Product Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={loading} className="flex-1 bg-[#B22222] hover:bg-[#8B0000]">
                {loading ? 'Saving...' : editingProduct ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductManagement;
